import React from 'react';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { Rows } from './Rows';
import { RowsGroup } from './RowsGroup';
import { Column, Group, ListingEvent, RenderRowDetail, StaticSettings } from '../types';

interface Props<Data extends Record<string, any>, Context extends any = any> {
  context: Context;
  tableData: Data[];
  loading?: boolean;
  groups?: Group<Data>[];
  emptyMessage?: React.ReactNode;
  staticSettings?: StaticSettings;
  columns: Column<Data, Context>[];
  onEmit?: (event: ListingEvent, data?: unknown) => void;
  renderRowDetail?: RenderRowDetail<Data, Context>;
}

export const TableBody = <Data extends Record<string, any>, Context extends any = any>({
  groups,
  onEmit,
  loading,
  columns,
  context,
  tableData,
  emptyMessage = 'No data available',
  staticSettings,
  renderRowDetail,
}: Props<Data, Context>) => {
  const isEmpty = groups ? groups.every(group => group.data.length === 0) : tableData.length === 0;

  if (loading && isEmpty)
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '12rem', padding: '2rem' }}
      >
        <CircularProgress color="primary" />
      </Box>
    );

  if (!loading && isEmpty)
    return (
      <Stack
        spacing={1}
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: '12rem', padding: '2rem', color: 'text.secondary' }}
      >
        <InboxOutlinedIcon fontSize="large" color="disabled" />
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Stack>
    );

  if (groups)
    return (
      <RowsGroup
        groups={groups}
        onEmit={onEmit}
        columns={columns}
        context={context}
        staticSettings={staticSettings}
        renderRowDetail={renderRowDetail}
        key={staticSettings?.allGroupsOpen?.toString()}
      />
    );

  return (
    <Box>
      <Rows tableData={tableData} context={context} columns={columns} renderRowDetail={renderRowDetail} />
    </Box>
  );
};
